import type { CustomRoute, CustomRoutes } from '@bo-types/Router';
import { flatten, pluck } from 'ramda';
import { ROUTES } from '@bo-components/navigation/Router';
import type { UserRoles } from '@bo-types/authentication';
import { rolesHavePermission } from '@bo-hooks/permissions/useHasRolePermission';

const getValidatedRoute = ({
  activeModules,
  route
}: {
  activeModules: Array<string>;
  route: CustomRoute;
}): CustomRoute => {
  const { childRoutes, featureFlagName = '', isFeatureFlagControlled } = route;

  const active =
    !isFeatureFlagControlled || activeModules.includes(featureFlagName);
  let activeChildRoutes: CustomRoutes = [];

  if (childRoutes?.length) {
    activeChildRoutes = childRoutes.map((childRoute) => {
      return getValidatedRoute({ activeModules, route: childRoute });
    });
  }

  return { ...route, active, childRoutes: activeChildRoutes };
};

export const getAllRoutes = ({
  availableRoutes
}: {
  availableRoutes?: string;
}) => {
  if (!availableRoutes) {
    return [];
  }

  const activeModules = availableRoutes?.split('-');

  const routes = ROUTES.map((group) => {
    const groupRoutes = group.routes.map((route) => {
      return getValidatedRoute({ activeModules, route });
    });

    return { ...group, routes: groupRoutes };
  });
  return routes;
};

export const getActiveRoutes = ({
  availableRoutes
}: {
  availableRoutes?: string;
}) => {
  const allRoutes = pluck('routes', getAllRoutes({ availableRoutes }));
  return flatten(allRoutes).filter((route) => route.active);
};

export const hasValidRoutePermission = ({
  route,
  userRoles
}: {
  route: CustomRoute | undefined;
  userRoles: UserRoles;
}): boolean => {
  if (!route) {
    return false;
  }

  return (
    !route.permissionName ||
    rolesHavePermission(userRoles, route.permissionName)
  );
};

export const getRoleActiveRoutes = ({
  availableRoutes,
  userRoles
}: {
  availableRoutes?: string;
  userRoles: UserRoles;
}) => {
  const allRoutes = getAllRoutes({ availableRoutes });

  const routeGroups = allRoutes.map((group) => {
    const groupRoutes = group.routes.filter((route) => {
      if (!route.active) {
        return false;
      }
      return hasValidRoutePermission({ route, userRoles });
    });

    return { ...group, routes: groupRoutes };
  });

  return routeGroups.filter((group) => group.routes.length);
};

export const getRoleActiveRoutePaths = ({
  availableRoutes,
  userRoles
}: {
  availableRoutes?: string;
  userRoles: UserRoles;
}) => {
  const groups = getRoleActiveRoutes({ availableRoutes, userRoles });
  const routes = flatten(pluck('routes', groups));
  return pluck('path', routes);
};
